import { MatchDecorator, WidgetType, EditorView, Decoration } from '@codemirror/view';
import { createRoot, type Root } from 'react-dom/client';
import React from 'react';
import { InputPill, type PillType } from './InputPill';
import { pillRegExp } from '@/utils/diff';

class PillWidget extends WidgetType {
  private root: Root | null = null;

  constructor(
    readonly type: PillType,
    readonly id: string,
    readonly ogText: string,
    private readonly latestCallbacks: React.MutableRefObject<any>,
  ) {
    super();
  }

  eq(other: PillWidget): boolean {
    // We compare all the data properties that define the pill.
    // If the type, id, and the underlying text are all the same,
    // then the widget is the same.
    return other.type === this.type && other.id === this.id && other.ogText === this.ogText;
  }

  toDOM(view: EditorView) {
    const span = document.createElement('span');
    span.className = 'cm-pill-widget-container';
    span.style.display = 'inline-block';
    span.style.verticalAlign = 'middle';

    // Initialize React root
    this.root = createRoot(span);

    const onRemove = () => {
      if (this.latestCallbacks.current?.onPillRemove) {
        this.latestCallbacks.current.onPillRemove(this.ogText);
      }
    };

    this.root.render(
      React.createElement(InputPill, {
        type: this.type,
        label: this.getLabel(),
        onRemove,
      }),
    );

    return span;
  }

  private getLabel() {
    // Basic formatting for the ID - can be extended to look up real names
    if (this.type === 'asset' && this.id.length > 20) {
      return this.id.substring(0, 8) + '...' + this.id.substring(this.id.length - 8);
    }

    if (this.type === 'figma') {
      return 'Figma Design';
    }

    if (this.type === 'image') {
      return `Image ${this.id}`;
    }

    if (this.type === 'annotation') {
      const tagMatch = this.id.match(/<([^>]+)>/);

      if (tagMatch && tagMatch[1].trim()) {
        return `<${tagMatch[1].trim()}>`;
      }

      return 'Selection';
    }

    return this.id;
  }

  destroy() {
    // Cleanup React root to prevent memory leaks
    if (this.root) {
      // Use a small delay or check to avoid "unmount during render" warnings if CM destroys quickly
      const root = this.root;
      setTimeout(() => root.unmount(), 0);
    }
  }

  ignoreEvent() {
    return true; // Handle events inside the React component
  }
}

export const createPillDecorator = (callbacksRef: React.MutableRefObject<any>) => {
  return new MatchDecorator({
    regexp: pillRegExp,
    decoration: (match, view, pos) => {
      // console.log('[Ano] match from pillDecorator: ', match);

      const type = match[1] as PillType;
      const id = match[2];
      const ogText = match[0];

      return Decoration.replace({
        widget: new PillWidget(type, id, ogText, callbacksRef),
        inclusive: false,
      });
    },
  });
};
